import { useCallback, useEffect, useRef, useState } from 'react'
import GrileService from '../../services/grile-service'
import { useMediaQuery } from 'react-responsive'
import { socket } from 'socket'
import { toast } from 'react-toastify'
import useGetAllTags from '../../hooks/useGetAllTags'
import { orderAnswerOptionsDescending, renderHtml } from 'utils'
import { settings } from 'settings'

const stableSort = (arr, compare) =>
  arr
    .map((item, index) => ({ item, index }))
    .sort((a, b) => compare(a.item, b.item) || a.index - b.index)
    .map(({ item }) => item)

const useSubject = (create = false) => {
  const testing = !!process.env.REACT_APP_TESTING
  const [height, setHeight] = useState(0)
  const [currentSubject, setCurrentSubject] = useState(undefined)
  const [answer, setAnswer] = useState(null)
  const [isLoadingSubject, setIsLoadingSubject] = useState(false)
  const [isSubmittingSubject, setIsSubmittingSubject] = useState(false)
  const [isSkippingSubject, setIsSkippingSubject] = useState(false)
  const [hint, setHint] = useState(Array(10).fill(undefined))
  const [isLoadingSubjectByCode, setIsLoadingSubjectByCode] = useState(false)
  const [time, setTime] = useState(null)
  const timerInterval = useRef(undefined)
  const isMounted = useRef(true)
  const [checkbox, setCheckbox] = useState([false, false, false, false])
  const [currentGridIndex, setCurrentGridIndex] = useState(0)

  const [subjectOptions, setSubjectOptions] = useState(undefined)
  const [dropdownValue, setDropdownValue] = useState(undefined)
  const [generateFaculty, setGenerateFaculty] = useState(undefined)
  const [allTags, setAllTags] = useState([])
  const [coursesForFilters, setCoursesForFilters] = useState({})
  const [selectedSubjectType, setSelectedSubjectType] = useState('')

  const [isSubmitted, setIsSubmitted] = useState(false)
  const { coursesWithChapters } = useGetAllTags()

  const [selectedTab, setSelectedTab] = useState(0)
  const [nextSubjectDate, setNextSubjectDate] = useState(null)
  const [participated, setParticipated] = useState(null)
  const [nextSubjectID, setNextSubjectID] = useState(null)
  const [lastSubjectRanking, setLastSubjectRanking] = useState(null)
  const [explainAnswers, setExplainAnswers] = useState()

  const [currentUser, setCurrentUser] = useState('JohnDoe')

  const handleTabChange = useCallback(
    (event, newSelectedTab) => {
      setSelectedTab(newSelectedTab)
    },
    [setSelectedTab],
  )

  const setTimeLocalStorage = useCallback(() => {
    localStorage.setItem('subjectId', time['subjectId'])
    localStorage.setItem('solving_time_subject', time['solving_time_subject'])
  }, [time])

  const setTimeBackend = async () => {
    const newTime = localStorage.getItem('solving_time_subject')
    const subjectId = localStorage.getItem('subjectId')
    if (newTime && subjectId) {
      socket.emit(
        'update_subject_solving_time',
        localStorage.getItem('token'),
        newTime,
        subjectId,
        response => {},
      )
    }
  }

  const handleGetRemainingTimeString = useCallback(() => {
    let remainingTime
    if (currentSubject.end_timestamp) {
      remainingTime = Math.max(
        0,
        currentSubject.end_timestamp - Date.now() / 1000,
      )
    } else {
      if (time === null) {
        remainingTime = 0
      } else {
        remainingTime = time['solving_time_subject']
      }
    }

    if (testing) {
      remainingTime = 6969
    }

    if (remainingTime < 3600) {
      return `${
        Math.floor((remainingTime % 3600) / 60) < 10 ? '0' : ''
      }${Math.floor((remainingTime % 3600) / 60)}:${
        Math.floor(remainingTime % 60) < 10 ? '0' : ''
      }${Math.floor(remainingTime % 60)}`
    } else {
      return `${Math.floor(remainingTime / 3600) < 10 ? '0' : ''}${Math.floor(
        remainingTime / 3600,
      )}:${Math.floor((remainingTime % 3600) / 60) < 10 ? '0' : ''}${Math.floor(
        (remainingTime % 3600) / 60,
      )}:${Math.floor(remainingTime % 60) < 10 ? '0' : ''}${Math.floor(
        remainingTime % 60,
      )}`
    }
  }, [time, testing, currentSubject])

  const handleStartTimer = () => {
    timerInterval.current = setInterval(() => {
      setTime(current => {
        if (!isMounted.current) return current

        return {
          ...current,
          solving_time_subject: current.solving_time_subject + 1,
        }
      })
    }, 1000)
  }

  const handleStopTimer = () => {
    timerInterval.current = clearInterval(timerInterval.current)
  }

  useEffect(() => {
    const handleChangeSelected = async () => {
      const inputs = Array.from(document.getElementsByClassName('choice'))

      if (inputs !== undefined && inputs.length > 0 && answer) {
        const selected = inputs.findIndex(input => input.checked === true) % 6
        inputs[selected].checked = true
      }
    }

    const inputs = Array.from(document.getElementsByClassName('choice'))

    inputs.forEach(input => {
      input.onclick = handleChangeSelected
    })
  }, [currentGridIndex, answer])

  const handleSubmit = useCallback(async () => {
    setIsSubmitted(true)

    const payload = {
      grile: currentSubject?.grile?.map(item => ({
        grila_id: item.id,
        answer: item.choice,
      })),
      subject_id: currentSubject?.subject_id,
    }

    await setTimeBackend()
    setIsSubmittingSubject(true)
    GrileService.submitSubject(payload)
      .then(response => {
        if (response) {
          setAnswer({
            correct_answer: response?.grile?.map(
              grila => grila?.correct_answer,
            ),
            explain: response?.grile?.map(grila => grila?.explain),
            solvingPercent: response?.solving_time_percent,
            averages: response?.medie_admitere,
            grile: response?.grile,
          })
          // Clear saved answers after successful submission
          localStorage.removeItem(
            `subject_answers_${currentSubject.subject_id}`,
          )
        }
      })
      .catch(error => console.error(error))
      .finally(() => setIsSubmittingSubject(false))

    handleStopTimer()
    setTimeLocalStorage()
    setCheckbox([false, false, false, false])
  }, [currentSubject, setTimeLocalStorage])

  const handleGetMaxLength = contentArray => {
    let max = 0

    contentArray.forEach(content => {
      let text = content
      let breakLineIndex = text?.indexOf('\n')
      while (text?.length > 0 && breakLineIndex !== -1) {
        const line = text?.substring(0, breakLineIndex)
        if (line.length > max) {
          max = line.length
        }

        text = text.substring(breakLineIndex + 1, text.length)
        breakLineIndex = text.indexOf('\n')
      }
      if (text?.length > max) {
        max = text.length
      }
    })

    return max
  }

  const isSmallDevice = useMediaQuery({
    query: '(max-width:1280px)',
  })

  useEffect(() => {
    isMounted.current = true

    if (time !== null && !time.stopped && timerInterval.current === undefined) {
      handleStartTimer()
    }

    if (time !== null) {
      setTimeLocalStorage()
      if ((time.solving_time_subject + 1) % 60 === 0) {
        setTimeBackend()
      }
    }

    return () => {
      isMounted.current = false

      if (timerInterval.current !== undefined) {
        clearInterval(timerInterval.current)
        timerInterval.current = undefined
      }
    }
  }, [time, setTimeLocalStorage])

  useEffect(() => {
    if (create === false) {
      GrileService.getAllFilters().then(response => {
        setAllTags(response['filters'])
      })

      window.addEventListener('beforeunload', setTimeBackend)
      return () => {
        window.removeEventListener('beforeunload', setTimeBackend)
        setTimeBackend()
      }
    }
  }, [create])

  const handleGetSubjectTimeString = time => {
    const nrOre = Math.floor(time / 3600)
    const nrMinute = Math.floor((time % 3600) / 60)
    return (
      String(nrOre) +
      (nrOre > 1 ? ' ore ' : ' oră ') +
      (nrMinute !== 0 ? `${String(nrMinute)} minute` : '')
    )
  }

  const setNextSimulare = useCallback(next_simulare => {
    setNextSubjectDate({
      start: next_simulare.begin_timestamp,
      end: next_simulare.end_timestamp,
    })
    setParticipated(next_simulare.participated)
    setNextSubjectID(next_simulare.id)

    if (next_simulare.clasament) {
      const clasament = next_simulare.clasament.map(user => {
        if (user.clasament_info[1] === null) {
          return null
        }
        const grileRezolvate = parseInt(user.clasament_info[0])
        const time = parseInt(user.clasament_info[1]) - user.begin_timestamp

        let timeString = handleGetSubjectTimeString(time)

        return {
          nickname: user.name,
          time: timeString,
          timeInt: time,
          grade: (grileRezolvate * 9) / 10 + 1,
        }
      })

      setLastSubjectRanking(
        clasament
          .filter(c => c !== null)
          .sort((a, b) => {
            if (a.grade === b.grade) {
              return a.timeInt - b.timeInt
            }

            return b.grade - a.grade
          }),
      )
    }
  }, [])

  const fetchNextSimulare = useCallback(() => {
    GrileService.getNextSimulare().then(response =>
      setNextSimulare(response.next_simulare),
    )
  }, [setNextSimulare])

  const setPickSubjectOptionsAndFilters = useCallback(
    subjects => {
      let options = []
      for (let index = 0; index < subjects.length; index++) {
        let option = ''
        option += subjects[index]['source'] || `Subiect${index}`
        if (
          subjects[index].id === -1 &&
          subjects[index].variants !== undefined
        ) {
          setCoursesForFilters(subjects[index].variants)
        }

        options.push({
          count: subjects?.[index]?.['grile_count'],
          time: handleGetSubjectTimeString(subjects?.[index]?.['solving_time']),
          faculties: subjects?.[index]?.['faculties'],
          value: subjects?.[index]?.['id'],
          label: option,
          materii: subjects?.[index]?.['materii'],
          medie_admitere: subjects?.[index]?.['medie_admitere'],
          isDisabled: subjects?.[index]?.['disabled'] === true,
          correct_answers: subjects?.[index]?.['correct_answers'],
        })
      }

      options = stableSort(options, (a, b) => {
        return a.isDisabled - b.isDisabled
      })

      setSubjectOptions(options)
      setDropdownValue(options[0])
    },
    [setSubjectOptions, setDropdownValue],
  )

  const findGridCourse = useCallback(
    tags => {
      for (const tag of tags) {
        for (const course in coursesWithChapters) {
          const coursesIds = coursesWithChapters[course].map(
            mappedCourse => mappedCourse.id,
          )
          if (coursesIds.includes(tag)) {
            return course
          }
        }
      }
      return null
    },
    [coursesWithChapters],
  )

  const handleChangeAnswer = choice => {
    setCurrentSubject(currentSubject => {
      const updatedGrids = [...currentSubject.grile]
      const currentGrid = currentSubject.grile[currentGridIndex]
      const userChoice = currentSubject?.grile[currentGridIndex]?.choice
      const isMultipleChoice = currentGrid.multiple_choice
      let newChoiceString = undefined
      if (isMultipleChoice) {
        const isInAnswer = String(userChoice).indexOf(choice.toString())

        if (isInAnswer !== -1) {
          newChoiceString =
            String(userChoice).substring(0, isInAnswer) +
            String(userChoice).substring(isInAnswer + 1)
        } else {
          newChoiceString =
            userChoice !== null ? String(userChoice) + choice : String(choice)
        }

        const newChoice = orderAnswerOptionsDescending(newChoiceString)
        updatedGrids[currentGridIndex] = {
          ...updatedGrids[currentGridIndex],
          choice: newChoice,
        }
      } else {
        updatedGrids[currentGridIndex] = {
          ...updatedGrids[currentGridIndex],
          choice: choice,
        }
      }

      // Save answers to local storage
      const subjectAnswers = updatedGrids.map(grid => ({
        id: grid.id,
        choice: grid.choice,
      }))
      localStorage.setItem(
        `subject_answers_${currentSubject.subject_id}`,
        JSON.stringify(subjectAnswers),
      )

      return {
        ...currentSubject,
        grile: updatedGrids,
      }
    })
  }

  const loadSavedAnswers = subjectId => {
    const savedAnswers = localStorage.getItem(`subject_answers_${subjectId}`)
    if (savedAnswers) {
      try {
        const parsedAnswers = JSON.parse(savedAnswers)
        setCurrentSubject(currentSubject => {
          if (!currentSubject) return currentSubject
          const updatedGrids = currentSubject.grile.map(grid => {
            const savedAnswer = parsedAnswers.find(
              answer => answer.id === grid.id,
            )
            return savedAnswer ? { ...grid, choice: savedAnswer.choice } : grid
          })
          return {
            ...currentSubject,
            grile: updatedGrids,
          }
        })
      } catch (error) {
        console.error('Error loading saved answers:', error)
        localStorage.removeItem(`subject_answers_${subjectId}`)
      }
    }
  }

  const handleStartSolving = useCallback(
    response => {
      setIsSubmitted(false)
      if (!isMounted.current) return
      if (response.id === -1) {
        setPickSubjectOptionsAndFilters(response.subjects_facultate)
        fetchNextSimulare()
        return
      }

      let newTime = null
      if (parseInt(localStorage.getItem('subjectId')) === response.subject_id) {
        newTime = localStorage.getItem('solving_time_subject')
      }
      if (newTime === null) {
        newTime = 0
      } else {
        newTime = parseInt(newTime)
      }

      if (response.solving_time > newTime) {
        newTime = response.solving_time
      }

      // Process grid data
      for (
        let grilaIndex = 0;
        grilaIndex < response.grile.length;
        grilaIndex++
      ) {
        const grila = JSON.parse(response.grile[grilaIndex].json)

        const newGrila = []
        for (let index = 0; index < grila.length; index++) {
          if (grila[index].type === 'matrix') {
            const matrixContainer = {
              type: 'matrix',
              content: [],
              maxSize: -1,
            }

            while (grila[index].type === 'matrix') {
              matrixContainer.content.push(grila[index].content)
              index += 1
            }

            matrixContainer.maxSize = handleGetMaxLength(
              matrixContainer.content,
            )
            newGrila.push(matrixContainer)

            if (index < grila.length) {
              newGrila.push(grila[index])
            }
          } else if (grila[index].type === 'text') {
            grila[index].content = renderHtml(grila[index].content)
            newGrila.push(grila[index])
          } else {
            newGrila.push(grila[index])
          }
        }
        response.grile[grilaIndex].json = newGrila
      }

      if (response.answered) {
        setTime({
          subjectId: response.subject_id,
          solving_time_subject: newTime,
          stopped: true,
        })
        setAnswer({
          correct_answer: response.grile.map(grila => grila.correct_answer),
          user: response.grile.map(grila => grila.choice),
          explain: response.grile.map(grila => grila.explain),
        })
      } else {
        setTime({
          subjectId: response.subject_id,
          solving_time_subject: newTime,
          stopped: false,
        })
        setAnswer(null)
      }

      const updatedGrids = response?.grile?.map(grid => ({
        ...grid,
        course: findGridCourse(grid.capitole) ?? grid.materie_copiat,
      }))

      const orderMaterii = settings.order_materii

      if (orderMaterii) {
        updatedGrids.sort(
          (a, b) =>
            orderMaterii.indexOf(a.course) - orderMaterii.indexOf(b.course),
        )
      } else {
        updatedGrids.sort((a, b) => a.course.localeCompare(b.course))
      }

      setCurrentSubject({ ...response, grile: updatedGrids })

      // Load saved answers after setting the current subject
      setTimeout(() => {
        loadSavedAnswers(response.subject_id)
      }, 0)
    },
    [
      setCurrentSubject,
      findGridCourse,
      setPickSubjectOptionsAndFilters,
      fetchNextSimulare,
    ],
  )

  const handleStartNewSubject = async (
    subject_id = -1,
    subject_code = undefined,
    similar_to = undefined,
  ) => {
    try {
      const isAccessedSubject = subject_id === -2
      if (!isAccessedSubject) {
        setIsLoadingSubject(true)
      } else {
        setIsLoadingSubjectByCode(true)
      }
      const response = await GrileService.getNewSubject(
        subject_id,
        subject_code,
        similar_to,
      )
      if (isAccessedSubject) {
        if (response?.subject_id) {
          handleStartSolving(response)
        } else {
          toast.error('Codul subiectului este invalid')
        }
      } else {
        handleStartSolving(response)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingSubject(false)
      setIsLoadingSubjectByCode(false)
    }
  }

  useEffect(() => {
    if (create === false && coursesWithChapters !== null) {
      GrileService.getSubject().then(response => handleStartSolving(response))
    }
  }, [coursesWithChapters, create, handleStartSolving])

  useEffect(() => {}, [dropdownValue])

  useEffect(() => {
    if (currentSubject !== undefined && currentSubject.grile !== undefined) {
      const newSubjectMaxLength = Array(currentSubject.grile.length).fill(0)

      for (let index = 0; index < currentSubject.grile.length; index++) {
        const variante = Array.from(
          currentSubject.grile[index].json?.filter(function (item) {
            return (
              typeof item?.type === 'string' && item?.type?.includes('choice-')
            )
          }) ?? [],
        )

        const contentVariante = []
        variante?.forEach(varianta => {
          contentVariante.push(varianta.content)
        })

        newSubjectMaxLength[index] = handleGetMaxLength(contentVariante)
      }
    }
  }, [currentSubject])

  const handleNextGrid = useCallback(() => {
    setCurrentGridIndex(prev => prev + 1)
  }, [setCurrentGridIndex])

  const handlePrevGrid = useCallback(() => {
    setCurrentGridIndex(prev => prev - 1)
  }, [setCurrentGridIndex])

  const handleCurrentGrid = useCallback(
    index => {
      setCurrentGridIndex(index)
    },
    [setCurrentGridIndex],
  )

  const handleUserGetHint = useCallback(
    async grilaId => {
      try {
        const response = await GrileService.getHint(grilaId)

        setHint(prevHints => {
          const newHints = [...prevHints]
          newHints[currentGridIndex] = response?.content || 'Nu exista hint'
          return newHints
        })
      } catch (error) {
        console.error('Error fetching hint:', error)
      }
    },
    [currentGridIndex, setHint],
  )

  const handleGetHint = useCallback(async () => {
    await handleUserGetHint(currentSubject?.grile?.[currentGridIndex]?.id)
  }, [currentSubject?.grile, currentGridIndex, handleUserGetHint])

  const handleNewSubjectWindow = useCallback(
    async action => {
      let response
      if (action === 'skip') {
        if (answer === null) {
          try {
            setIsSkippingSubject(true)
            response = await GrileService.skipSubject()
          } catch (error) {
            console.error(error)
          } finally {
            setIsSkippingSubject(false)
          }
        }
      } else {
        setIsSkippingSubject(true)
        try {
          response = await GrileService.getSubject()
        } catch (error) {
          console.error(error)
        }
      }

      // Clear saved answers when starting a new subject
      if (currentSubject?.subject_id) {
        localStorage.removeItem(`subject_answers_${currentSubject.subject_id}`)
      }

      setAnswer(null)
      setCurrentGridIndex(0)
      setCurrentSubject(undefined)
      setHint(Array(10).fill(undefined))
      setHeight(0)

      setPickSubjectOptionsAndFilters(response.subjects_facultate)
      fetchNextSimulare()

      setIsSkippingSubject(false)
    },
    [
      answer,
      setPickSubjectOptionsAndFilters,
      currentSubject,
      fetchNextSimulare,
    ],
  )

  const handleChangeCoursesForFilters = useCallback(event => {
    const regexOnlyNumbers = /^-?(0|[1-9]\d*)$/
    const { name, value } = event.target
    if (!regexOnlyNumbers.test(value) && value !== '') {
      return
    }
    setCoursesForFilters(prev => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  useEffect(() => {
    if (dropdownValue) {
      setSelectedSubjectType(Object.keys(dropdownValue?.value)[0])
      setGenerateFaculty(undefined)
    }
  }, [dropdownValue])

  useEffect(() => {
    if (answer?.grile) {
      const currentGrila = answer.grile.find(
        g => g.grila_id === currentSubject?.grile[currentGridIndex]?.id,
      )

      if (currentGrila.explain_answers) {
        setExplainAnswers(JSON.parse(currentGrila.explain_answers))
      } else {
        setExplainAnswers(null)
      }
    }
  }, [currentGridIndex, answer, currentSubject])

  const handleSaveGrid = async () => {
    try {
      const payload = { grila_id: currentSubject.grile[currentGridIndex].id }
      await GrileService.saveGrid(payload)
      const currentSubjectCopy = { ...currentSubject }
      currentSubjectCopy.grile[currentGridIndex].is_saved = true
      setCurrentSubject(currentSubjectCopy)
    } catch (error) {
      console.error(error)
    }
  }

  const handleRemoveSavedGrid = async () => {
    try {
      const payload = { grila_id: currentSubject.grile[currentGridIndex].id }
      await GrileService.removeSavedGrid(payload)
      const currentSubjectCopy = { ...currentSubject }
      currentSubjectCopy.grile[currentGridIndex].is_saved = false
      setCurrentSubject(currentSubjectCopy)
    } catch (error) {
      console.error(error)
    }
  }

  return {
    nextSubjectID,
    currentUser,
    lastSubjectRanking,
    nextSubjectDate,
    participated,
    handleTabChange,
    selectedTab,
    answer,
    hint,
    currentSubject,
    handleGetHint,
    checkbox,
    currentGridIndex,
    setCheckbox,
    handleChangeAnswer,
    handleGetRemainingTimeString,
    handleSubmit,
    isSubmittingSubject,
    handleCurrentGrid,
    handlePrevGrid,
    handleNextGrid,
    isLoadingSubject,
    dropdownValue,
    handleNewSubjectWindow,
    isSubmitted,
    allTags,
    setCurrentSubject,
    isSmallDevice,
    subjectOptions,
    handleStartNewSubject,
    setDropdownValue,
    generateFaculty,
    setGenerateFaculty,
    height,
    setHeight,
    isSkippingSubject,
    isLoadingSubjectByCode,
    coursesForFilters,
    handleChangeCoursesForFilters,
    setSelectedSubjectType,
    selectedSubjectType,
    handleSaveGrid,
    handleRemoveSavedGrid,
    explainAnswers,
  }
}

export default useSubject
